import { clampSearchParamsQuery } from 'constants/search';
import type { GetPublicFeedRequestQuery, GetPublicFeedResponse, GetPublicHomeResponse } from 'lib/bff';
import { homeQueryString, type HomeRequest } from 'lib/bff/homeSelection';
import type { EventVo } from 'models/event';
import type {
    HistoricalDataPeriodInterval,
    MarketCandlestickVo,
    MarketForecastHistoryVo,
} from 'models/historical_data';
import type { MilestoneType, StructuredTargetVo } from 'models/milestones';
import type {
    GetEventsResponse,
    GetFiltersBySportsResponse,
    GetKeywordsResponse,
    GetLiveDataFeedRequest,
    GetMilestonesRequest,
    GetMilestoneVolumeResponse,
} from 'models/reqres';
import type { SearchSeriesParams, SearchSeriesResultVo } from 'models/search';
import type { SeriesVo } from 'models/series';
// Per-datasource `details` shapes for the shared /v1/live_data/events endpoint (type-only,
// erased at build — no runtime coupling to the feature modules).
import type { ArtistStreamsLiveDataDetails } from 'react_pages/Event/components/Charts2/artistStreamsChartUtils';
import type { WeatherLiveDataDetails } from 'react_pages/Event/components/Charts2/weatherChartUtils';
import type { CollectibleLiveDataDetails } from 'react_pages/Event/components/Collectible/useCollectibleLiveData';
import type { NetflixLiveDataDetails } from 'react_pages/Event/components/Netflix/useNetflixLiveData';
import type { RottenTomatoesLiveDataDetails } from 'react_pages/Event/components/RottenTomatoes/useRottenTomatoesLiveData';
import type { SocialMediaLiveDataDetails } from 'react_pages/Event/components/SocialMedia/useSocialMediaLiveData';
import { enrichHomeResponseWithRelations } from 'utils/cardEnrichment';
import config from 'utils/config';
import { drainResponseBody } from 'utils/drainResponseBody';
import logProviderFactory from 'utils/logger/logProviderFactory';
import { mostActiveStatus } from 'utils/markets';
import { buildQueryString } from 'utils/query';

import {
    fetchServerSide,
    isRetryableStatus,
    UpstreamResponseError,
    type FetchServerSideBehavior,
} from './fetchServerSide';

// Get the latest close date from all markets in an event (matches Event.closeDate in domain store)
// Returns ISO 8601 format: YYYY-MM-DDTHH:MM:SSZ (no milliseconds)
const getCloseDate = (event: EventVo): string | undefined => {
    const times = event.markets
        .map((m) => m.close_date)
        .filter(Boolean)
        .map((t) => new Date(t).getTime())
        .filter((t) => !isNaN(t));

    if (times.length === 0) return undefined;

    const latest = Math.max(...times);
    const iso = new Date(latest).toISOString();
    return iso.slice(0, 19) + 'Z';
};

// There is no top-level event open date — it lives on the markets. The earliest
// market `open_date` is effectively the event's open date. Mirrors `getCloseDate`
// but takes the MIN of the valid market open timestamps.
export const getOpenDate = (event: EventVo): string | undefined => {
    const times = event.markets
        .map((m) => m.open_date)
        .filter(Boolean)
        .map((t) => new Date(t).getTime())
        .filter((t) => !isNaN(t));

    if (times.length === 0) return undefined;

    const earliest = Math.min(...times);
    const iso = new Date(earliest).toISOString();
    return iso.slice(0, 19) + 'Z';
};

// True when any market is still reported as `initialized` (not yet open for trading)
// even though its open date has already passed — the hallmark of a stale pre-open
// snapshot served from the Next data cache. This is the exact condition `fetchEvent`
// uses to trigger its uncached re-fetch, exported so callers that opt OUT of that
// re-fetch (`allowUncachedRefetch: false`) can still recognise a stale event and avoid
// treating it as fresh (see `CalendarPageContent`).
export const hasPendingPastOpenMarket = (event: EventVo): boolean =>
    event.markets.some((market) => {
        const openMs = new Date(market.open_date).getTime();
        if (isNaN(openMs)) return false;
        return openMs <= Date.now() && market.status === 'initialized';
    });

export const fetchSeriesByKeyword = (keyword: string) =>
    fetchServerSide(`${config.exchangeApiUrl}/v1/search/series?keywords=${keyword}`, {
        // Revalidate every 1 hour
        next: { revalidate: 3600 },
    });

const KEYWORDS_PAGE_SIZE = 100;

type GetKeywordsPageResponse = GetKeywordsResponse & {
    cursor?: string;
};

// Exhaustive fetch — drains every keyword page. The tag landing page validates
// the URL keyword against the full active-keyword corpus, so it must see all of
// them (a valid keyword beyond the first page would otherwise falsely 404).
export const fetchKeywords = async () => {
    const keywords: GetKeywordsResponse['keywords'] = [];
    let cursor: string | undefined;

    for (;;) {
        const search = new URLSearchParams({
            limit: String(KEYWORDS_PAGE_SIZE),
        });
        if (cursor) search.set('cursor', cursor);

        const response = await fetchServerSide(`${config.exchangeApiUrl}/v1/keywords/?${search.toString()}`, {
            // Revalidate every 1 hour
            next: { revalidate: 3600 },
        });
        if (!response?.ok) return response;

        const data = (await response.json()) as GetKeywordsPageResponse;
        keywords.push(...(data.keywords ?? []));

        const nextCursor = data.cursor || undefined;
        if (!nextCursor || nextCursor === cursor) {
            const payload: GetKeywordsResponse = { keywords };
            return new Response(JSON.stringify(payload), {
                headers: { 'content-type': 'application/json' },
                status: 200,
            });
        }
        cursor = nextCursor;
    }
};

// Footer-only capped variant — a single request fetching at most 50 keywords (no
// pagination). The pre-footer "Related topics" column only shuffles 20 out of
// these, so draining the full corpus (as exhaustive `fetchKeywords` does for the
// tag page) isn't worth the cost. The caller reads `keywords` off the JSON.
export const fetchFooterKeywords = () =>
    fetchServerSide(`${config.exchangeApiUrl}/v1/keywords/?limit=50`, {
        // Revalidate every 1 hour
        next: { revalidate: 3600 },
    });

export const fetchSeriesByTickers = async (seriesTickers: string[]) => {
    const response = await fetchServerSide(
        `${config.exchangeApiUrl}/v1/series/${buildQueryString({ series_tickers: seriesTickers })}`,
        {
            // Revalidate every 1 hour
            next: { revalidate: 3600 },
        },
    );
    if (!response?.ok) {
        // Status is all we need; release the error body instead of leaving it queued.
        drainResponseBody(response);
        return [];
    }
    const data = await response.json();
    return data.series || [];
};

export const fetchCategoryNavigation = async () => {
    return fetchServerSide(`${config.exchangeApiUrl}/v1/bff/category_navigation`, {
        // Revalidate every 1 hour
        next: { revalidate: 120 },
    });
};

export const fetchMarketById = (marketId: string, options: { revalidate?: number } = {}) =>
    fetchServerSide(`${config.exchangeApiUrl}/v1/markets/${marketId}`, {
        next: { revalidate: options.revalidate ?? 3600 },
    });

export const fetchMarketByTicker = (marketTicker: string, options: { revalidate?: number } = {}) =>
    fetchServerSide(`${config.exchangeApiUrl}/v1/markets_by_ticker/${marketTicker}`, {
        next: { revalidate: options.revalidate ?? 3600 },
    });

export const fetchMilestoneById = (id: string, behavior?: FetchServerSideBehavior) => {
    const url = `${config.exchangeApiUrl}/v1/milestones/${id}`;
    const options = { next: { revalidate: 300 } };
    return behavior ? fetchServerSide(url, options, behavior) : fetchServerSide(url, options);
};

export const MILESTONE_VOLUME_REVALIDATE_SECONDS = 60;

export const fetchMilestoneVolume = async (
    milestoneId: string,
    options?: { signal?: AbortSignal },
): Promise<GetMilestoneVolumeResponse | undefined> => {
    const url = `${config.exchangeApiUrl}/v1/milestones/${milestoneId}/volume`;
    try {
        const response = await fetchServerSide(url, {
            // A route's revalidate is the MINIMUM of its own and every fetch it makes, so a
            // shorter interval here silently halves the ISR lifetime of any page that seeds
            // milestone volume. Kept at the market route's own 60s for that reason; the
            // consumer hook polls every 5s, so a staler seed self-corrects after hydration.
            next: { revalidate: MILESTONE_VOLUME_REVALIDATE_SECONDS },
            signal: options?.signal,
        });
        if (!response?.ok) {
            drainResponseBody(response);
            return undefined;
        }
        return response.json();
    } catch (e) {
        logProviderFactory.logMessage(`Error fetching milestone volume for ${milestoneId}: ${e}`);
        return undefined;
    }
};

export const fetchMilestones = (
    params: GetMilestonesRequest,
    allowCaching = true,
    behavior?: FetchServerSideBehavior,
) => {
    const url = `${config.exchangeApiUrl}/v1/milestones${buildQueryString(params)}`;
    const options = allowCaching ? { next: { revalidate: 300 } } : undefined;
    return behavior ? fetchServerSide(url, options, behavior) : fetchServerSide(url, options);
};

export const fetchMilestoneLiveData = (type: MilestoneType, milestoneId: string) =>
    fetchServerSide(`${config.exchangeApiUrl}/v1/live_data/${type}/milestone/${milestoneId}`, {
        // Revalidate every 15 seconds, as live data can be very volatile and we want to keep it as up-to-date as possible without overwhelming the server with requests
        next: { revalidate: 15 },
    });

export const fetchMilestoneLiveDataBatch = async (milestonesIds: string[]) => {
    // The endpoint expexts multiple separated milestone_ids params, not comma separated
    const parsedIds = milestonesIds.map((id) => id).join('&milestone_ids=');
    const response = await fetchServerSide(`${config.exchangeApiUrl}/v1/live_data/batch?milestone_ids=${parsedIds}`, {
        // Revalidate every 15 seconds, as live data can be very volatile and we want to keep it as up-to-date as possible without overwhelming the server with requests
        next: { revalidate: 15 },
    });

    if (!response?.ok) {
        drainResponseBody(response);
        return null;
    }

    return response.json();
};

// The `details` payload of /v1/live_data/events is one of these per-datasource shapes,
// chosen by the `type` discriminator. `type` stays an open string (the backend can add
// datasources we don't model yet); consumer hooks narrow `details` via their own `select`.
export type EventLiveDataDetails =
    | RottenTomatoesLiveDataDetails
    | NetflixLiveDataDetails
    | WeatherLiveDataDetails
    | ArtistStreamsLiveDataDetails
    | CollectibleLiveDataDetails
    | SocialMediaLiveDataDetails;

export interface EventLiveDataResponse {
    type: string | undefined;
    details: EventLiveDataDetails | undefined;
}

interface EventLiveDataEndpointResponse {
    live_data?: {
        type?: string;
        details?: EventLiveDataDetails;
    };
}

// Fetches the shared `/v1/live_data/events/{ticker}` endpoint that backs every
// event live datasource (Rotten Tomatoes, Netflix, weather, artist streams,
// collectibles, social media). Server-callable; returns the raw `{ type, details }`.
// `revalidate` is the Next cache TTL in seconds and must match the consuming hook's
// client poll cadence — caching longer than the poll interval hydrates live data the
// minute-polled hooks render as already-fresh, so they show stale counts/charts until
// their first interval tick. The caller derives it per datasource type.
export const fetchEventLiveData = async (
    eventTicker: string,
    revalidate: number,
): Promise<EventLiveDataResponse | undefined> => {
    const url = `${config.exchangeApiUrl}/v1/live_data/events/${encodeURIComponent(eventTicker)}`;
    try {
        const response = await fetchServerSide(url, {
            next: { revalidate },
        });
        if (!response?.ok) {
            drainResponseBody(response);
            return undefined;
        }
        const data = (await response.json()) as EventLiveDataEndpointResponse;
        return { type: data.live_data?.type, details: data.live_data?.details };
    } catch (e) {
        logProviderFactory.logError(`fetchEventLiveData error for ${eventTicker}: ${e}`);
        return undefined;
    }
};

export const fetchLiveDataFeed = (params: GetLiveDataFeedRequest): Promise<Response | null> =>
    fetchServerSide(`${config.exchangeApiUrl}/v1/live_data/feed${buildQueryString(params)}`, {
        // Revalidate every 2 minutes
        next: { revalidate: 120 },
    });

export const fetchLiveDataFeedFirstPage = () =>
    fetchLiveDataFeed({
        include_events: true,
        include_markets: true,
        include_series: true,
        live_only: false,
        hydrate: 'structured_targets',
        page_size: 50,
    });

export const fetchLiveDataFiltersV2 = (): Promise<Response | null> =>
    fetchServerSide(`${config.exchangeApiUrl}/v1/live_data/filters_v2`, {
        // Revalidate every 2 minutes
        next: { revalidate: 120 },
    });

export const fetchLiveDataCount = (): Promise<Response | null> =>
    fetchServerSide(`${config.exchangeApiUrl}/v1/live_data/count`, {
        // Revalidate every 2 minutes
        next: { revalidate: 120 },
    });

export const fetchStructuredTargetById = (id: string) =>
    fetchServerSide(`${config.exchangeApiUrl}/v1/structured_targets/${id}`, {
        // Revalidate every 5 minutes
        next: { revalidate: 300 },
    });

// Structured-target hub live data (team_stats / artist_stats payloads).
// The backend poller refreshes these hourly (artists) / half-hourly
// (teams); 5 minutes keeps SSR hydration close to the client hook's poll
// cadence without hammering the endpoint.
// `season` selects the standings/stats year (e.g. 2026). Omitted, the backend
// serves the current one; a finished year is needed alongside it to describe a
// team's last season before the new one has games.
export const fetchStructuredTargetLiveData = (structuredTargetId: string, season?: number) => {
    const query = season ? `?season=${season}` : '';
    return fetchServerSide(`${config.exchangeApiUrl}/v1/live_data/structured_targets/${structuredTargetId}${query}`, {
        // Revalidate every 5 minutes
        next: { revalidate: 300 },
    });
};

export const fetchStructuredTargetsByNameAndType = (name: string, type: string, page_size: number = 5) => {
    const url = `${config.exchangeApiUrl}/v1/structured_targets/${buildQueryString({
        name,
        type,
        page_size,
    })}`;
    return fetchServerSide(url, {
        // Revalidate every 2 minutes
        next: { revalidate: 120 },
    });
};

const mapTeamTypeToPlayerType = (transformedType: string): string => {
    if (!transformedType) {
        return transformedType;
    }

    if (transformedType.includes('_team')) {
        return transformedType.replaceAll('_team', '_player');
    }

    return transformedType.replaceAll('team', 'player');
};

export const fetchStructuredTargetsByTypeAndTeamId = (
    transformedType: string,
    team_id: string,
    page_size: number = 100,
) => {
    const playerType = mapTeamTypeToPlayerType(transformedType);
    const url = `${config.exchangeApiUrl}/v1/structured_targets/${buildQueryString({
        type: playerType,
        team_id,
        page_size,
    })}`;
    return fetchServerSide(url, {
        // Revalidate every 2 minutes
        next: { revalidate: 120 },
    });
};

const fetchMultipleStructuredTargets = async (ids: string[]): Promise<StructuredTargetVo[]> => {
    const newStructuredTargets: StructuredTargetVo[] = [];
    let cursor: string | undefined = undefined;
    let ended = false;

    // The endpoint expexts multiple separated ids params, not comma separated
    const parsedIds = ids.map((id) => id).join('&ids=');

    try {
        while (!ended) {
            const response = await fetchServerSide(
                `${config.exchangeApiUrl}/v1/structured_targets?page_size=1000&ids=${parsedIds}${!!cursor ? `&cursor=${cursor}` : ''}`,
                {
                    // Revalidate every 5 minutes
                    next: { revalidate: 300 },
                },
            );

            if (!response?.ok) {
                drainResponseBody(response);
                break;
            }

            const data = await response.json();
            const targets = data.structured_targets ?? [];
            newStructuredTargets.push(...targets);

            if (data.cursor) {
                cursor = data.cursor;
                continue;
            }

            ended = true;
        }

        return newStructuredTargets;
    } catch (e) {
        const error = `Error fetching server-side data on All Structured Targets: ${e}`;
        logProviderFactory.logError(error);
        return newStructuredTargets;
    }
};

export const fetchMultipleStructuredTargetsInChunks = async (ids: string[]): Promise<StructuredTargetVo[]> => {
    const CHUNK_SIZE = 150;

    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
        chunks.push(ids.slice(i, i + CHUNK_SIZE));
    }

    const resolvePromises = [];
    let allStructuredTargets: StructuredTargetVo[] = [];

    for (const chunk of chunks) {
        resolvePromises.push(fetchMultipleStructuredTargets(chunk));
    }

    const results = await Promise.all(resolvePromises);
    for (const result of results) {
        allStructuredTargets = allStructuredTargets.concat(result);
    }

    return allStructuredTargets;
};

export const FILTER_BY_SPORTS_TAG = 'filter-by-sports';

export const fetchFilterBySports = async (): Promise<GetFiltersBySportsResponse | null> => {
    const response = await fetchServerSide(
        `${config.exchangeApiUrl}/v1/search/filters_by_sport?with_counts=true&with_recent_volume=true`,
        { next: { revalidate: 120, tags: [FILTER_BY_SPORTS_TAG] } },
    );
    if (!response?.ok) {
        drainResponseBody(response);
        return null;
    }
    return response.json();
};

export type CryptoScopeInfo = { count: number; name: string; link_to_perps_page?: boolean; link_to_market?: string };
export type CryptoFilterItem = { count: number; scopes: Record<string, CryptoScopeInfo>; image_url?: string };
export type FiltersByCryptoResponse = {
    filters_by_crypto: Record<string, CryptoFilterItem>;
    crypto_ordering: string[];
    scope_ordering: string[];
};

/** Fetches BFF crypto category filters, optionally including margin/perps data. */
export const fetchCryptoCategoryFilters = async (
    options: { includeMargin?: boolean } = {},
): Promise<FiltersByCryptoResponse | undefined> => {
    const queryString = buildQueryString({
        category: 'Crypto',
        include_margin: options.includeMargin === true ? true : undefined,
    });
    const response = await fetchServerSide(`${config.exchangeApiUrl}/v1/bff/category_filters${queryString}`, {
        next: { revalidate: 120 },
    });
    if (!response?.ok) {
        drainResponseBody(response);
        return undefined;
    }
    const raw: { category_filters?: { Crypto?: FiltersByCryptoResponse } } = await response.json();
    return raw.category_filters?.Crypto;
};

export type CommodityScopeInfo = { count: number; name: string; image_url?: string };
export type CommoditySubcategoryItem = { count: number; scopes: Record<string, CommodityScopeInfo> };
export type FiltersByCommoditiesResponse = {
    filters_by_subcategory: Record<string, CommoditySubcategoryItem>;
    subcategory_ordering: string[];
    scope_ordering: string[];
    filter_frequencies?: { name: string; count: number }[];
};

export const fetchFiltersByCommodities = async (): Promise<FiltersByCommoditiesResponse | undefined> => {
    const response = await fetchServerSide(`${config.exchangeApiUrl}/v1/bff/category_filters?category=Commodities`, {
        // Revalidate every 2 minutes
        next: { revalidate: 120 },
    });
    if (!response?.ok) {
        drainResponseBody(response);
        return undefined;
    }
    const data = await response.json();
    return data?.category_filters?.Commodities;
};

export const fetchSingleSeries = (seriesTicker: string, behavior?: FetchServerSideBehavior) => {
    const url = `${config.exchangeApiUrl}/v1/series/${buildQueryString({ series_tickers: [seriesTicker] })}`;
    const options = { next: { revalidate: 300 } };
    return behavior ? fetchServerSide(url, options, behavior) : fetchServerSide(url, options);
};

export const fetchSingleSeriesVo = async (seriesTicker: string): Promise<SeriesVo | undefined> => {
    const url = `${config.exchangeApiUrl}/v1/series/${buildQueryString({ series_tickers: [seriesTicker] })}`;

    let seriesVo: SeriesVo | undefined = undefined;

    try {
        const seriesResponse = await fetchServerSide(url, {
            // Revalidate every 5 minutes
            next: { revalidate: 300 },
        });
        if (seriesResponse?.ok) {
            const seriesReturn = await seriesResponse.json();
            if (!!seriesReturn.series?.length) {
                seriesVo = seriesReturn.series[0];
            }
        }
    } catch (e) {
        const error = `Error fetching server-side data on Single Series from ${url}: ${e}`;
        logProviderFactory.logError(error);
    }

    return seriesVo;
};

export const fetchSearchSeries = async (
    params: SearchSeriesParams,
    makeSingleRequest?: boolean,
    options: { retryTransientErrors?: boolean; throwOnError?: boolean } = {},
): Promise<SearchSeriesResultVo> => {
    // Clamped because the endpoint answers an over-long `query` with a 400 the search can only
    // repeat — see `clampSearchParamsQuery`.
    const base_url = `${config.exchangeApiUrl}/v1/search/series${buildQueryString(clampSearchParamsQuery(params))}`;
    let urlWithCursor = base_url;
    const newSearchSeriesVo: SearchSeriesResultVo = { current_page: [], next_cursor: '', total_results_count: 0 };

    // How the catch below reports a failure. Downgraded only for the transient failures a
    // caller opted into retrying, which are routine against a live backend:
    //  - 'silent': `fetchServerSide` resolved to `null`, which it only does after logging
    //    the transient error it swallowed — logging again double-counts one network blip.
    //  - 'warning': the retry schedule was exhausted against a 429/5xx, which
    //    `fetchServerSide` hands back as-is. Worth recording, but not as an alerting error.
    // Every other failure — a non-retryable status, unparseable JSON, an unusable payload —
    // is unreported and unexpected, so it keeps 'error' and stays visible as a backend or
    // schema regression.
    let failureReport: 'error' | 'silent' | 'warning' = 'error';

    const fetchBehavior = options.retryTransientErrors
        ? { failureLogLevel: 'warning' as const, retryTransientErrors: true }
        : undefined;

    try {
        let nextCursor = '';

        while (true) {
            const eventsResponse = await fetchServerSide(
                urlWithCursor,
                {
                    // Revalidate every 2 minutes
                    next: { revalidate: 120 },
                },
                fetchBehavior,
            );

            if (!eventsResponse?.ok) {
                if (fetchBehavior) {
                    if (!eventsResponse) failureReport = 'silent';
                    else if (isRetryableStatus(eventsResponse.status)) failureReport = 'warning';
                }
                if (options.throwOnError) {
                    // Carries the status rather than folding it into the message, so a caller can
                    // tell a transient outage apart from a rejection the search will repeat
                    // verbatim — a WAF block on the query string, a param the endpoint refuses.
                    // Rethrowing the second forever turns a route into a permanent 500;
                    // `isFinalUpstreamError` is how a caller recognizes it and answers differently.
                    throw new UpstreamResponseError(
                        `Search Series request failed${eventsResponse ? ` with status ${eventsResponse.status}` : ''}`,
                        eventsResponse?.status,
                    );
                }
                break;
            }

            const eventsData = await eventsResponse.json();

            // A `200` whose body has no `current_page` array is a partial/malformed
            // payload (a truncated response, an upstream error page that still parsed),
            // not an empty result set. The `?? []` normalization below erases that
            // distinction, so this is the last point where the two are still
            // distinguishable — a cached caller would otherwise store the phantom empty
            // grid, or a downstream 404, for the whole revalidate window. An *empty*
            // `current_page` array stays a legitimate "no results" and does not throw.
            if (options.throwOnError && !Array.isArray(eventsData?.current_page)) {
                throw new Error(`Search Series returned an unusable payload from ${urlWithCursor}`);
            }

            // The pagination metadata needs the same treatment. `next_cursor ?? ''` and
            // `total_results_count ?? 0` below turn a partial `{ current_page: [...] }` into a
            // page that looks complete but reports no cursor and no total — so a cached grid
            // stores it with infinite scroll switched off for the whole revalidate window even
            // though more markets exist. Check the types, not truthiness: `next_cursor: ''` is
            // the legitimate last page and `total_results_count: 0` a legitimate empty result.
            if (options.throwOnError) {
                const cursorIsUsable =
                    eventsData.next_cursor === undefined ||
                    eventsData.next_cursor === null ||
                    typeof eventsData.next_cursor === 'string';
                if (typeof eventsData.total_results_count !== 'number' || !cursorIsUsable) {
                    throw new Error(`Search Series returned unusable pagination metadata from ${urlWithCursor}`);
                }
            }

            if (newSearchSeriesVo) {
                const returnedEvents = eventsData.current_page ?? [];
                newSearchSeriesVo.current_page.push(...returnedEvents);

                if (!newSearchSeriesVo.hydrated_data) {
                    newSearchSeriesVo.hydrated_data = eventsData.hydrated_data;
                } else {
                    // Merge milestones data
                    if (!newSearchSeriesVo.hydrated_data.milestones) {
                        newSearchSeriesVo.hydrated_data.milestones = eventsData.hydrated_data?.milestones;
                    } else {
                        // Merge existing milestones
                        Object.keys(eventsData.hydrated_data?.milestones ?? {}).map((key) => {
                            if (!newSearchSeriesVo.hydrated_data?.milestones || !eventsData.hydrated_data?.milestones) {
                                return;
                            }
                            newSearchSeriesVo.hydrated_data.milestones[key] = eventsData.hydrated_data.milestones[key];
                        });
                    }

                    // Merge structured targets data
                    if (!newSearchSeriesVo.hydrated_data.structured_targets) {
                        newSearchSeriesVo.hydrated_data.structured_targets =
                            eventsData.hydrated_data?.structured_targets;
                    } else {
                        // Merge existing structured targets
                        Object.keys(eventsData.hydrated_data?.structured_targets ?? {}).map((key) => {
                            if (
                                !newSearchSeriesVo.hydrated_data?.structured_targets ||
                                !eventsData.hydrated_data?.structured_targets
                            ) {
                                return;
                            }
                            newSearchSeriesVo.hydrated_data.structured_targets[key] =
                                eventsData.hydrated_data.structured_targets[key];
                        });
                    }
                }
            }

            // An absent/empty cursor means "last page" — but only if the pages we have actually
            // account for `total_results_count`. A partial 200 that drops the cursor while more
            // results exist makes the first page look terminal, so `next_cursor ?? ''` below seeds
            // a cached grid with a truncated list and infinite scroll switched off for the whole
            // revalidation window. Compares the ACCUMULATED length so the multi-request path's
            // genuine last page (cursorless, but complete) still passes.
            if (options.throwOnError) {
                const cursor = eventsData.next_cursor;
                const hasMorePages = typeof cursor === 'string' && cursor.length > 0;
                if (!hasMorePages && newSearchSeriesVo.current_page.length < eventsData.total_results_count) {
                    throw new Error(
                        `Search Series returned a truncated page (${newSearchSeriesVo.current_page.length} of ` +
                            `${eventsData.total_results_count} results, no cursor) from ${urlWithCursor}`,
                    );
                }
            }

            if (makeSingleRequest) {
                // If we only want a single request, preserve the cursor for pagination
                newSearchSeriesVo.next_cursor = eventsData.next_cursor ?? '';
                newSearchSeriesVo.total_results_count = eventsData.total_results_count ?? 0;
                break;
            }

            nextCursor = eventsData.next_cursor;
            if (!nextCursor) break;

            urlWithCursor = `${base_url}&cursor=${nextCursor}`;
        }
    } catch (e) {
        const error = `Error fetching server-side data on Search Series from ${urlWithCursor}: ${e}`;
        if (failureReport === 'error') {
            logProviderFactory.logError(error);
        } else if (failureReport === 'warning') {
            logProviderFactory.logWarning(error);
        }
        if (options.throwOnError) throw e;
    }

    return newSearchSeriesVo;
};

// Load and resolve events from server side. Don't load too many events, Netlify will timeout.
export const fetchEvents = async (
    eventsVo: EventVo[],
    tickers?: string[],
    series_ticker?: string,
    allowCaching?: boolean,
    fetchAll = false,
    // `throwOnFetchError` surfaces a transient HTTP failure (null/non-OK response
    // or network error) as a throw instead of a silent `[]`, so callers can tell a
    // failed fetch apart from a genuinely-empty result. Default off — existing
    // callers keep the swallow-to-`[]` behavior.
    options?: {
        excludeNestedMarkets?: boolean;
        status?: string;
        pageSize?: number;
        throwOnFetchError?: boolean;
        retryTransientErrors?: boolean;
    },
) => {
    const unloadedTickers = tickers?.filter((ticker) => !eventsVo.find((e) => e.ticker === ticker));
    if (!unloadedTickers?.length && !series_ticker) {
        return [];
    }

    let url = `${config.exchangeApiUrl}/v1/events`;

    if (unloadedTickers) {
        url = `${url}?page_size=500&tickers=${unloadedTickers.join(',')}`;
    } else if (series_ticker) {
        url = `${url}?series_ticker=${series_ticker}${fetchAll ? '' : '&single_event_per_series=true'}`;
        if (options?.status) url = `${url}&status=${options.status}`;
        if (options?.pageSize) url = `${url}&page_size=${options.pageSize}`;
    }

    if (options?.excludeNestedMarkets) {
        url = `${url}&exclude_nested_markets=true`;
    }

    let newEventsVo: EventVo[] = [];

    let fetchOptions: RequestInit | undefined;
    if (allowCaching === false) {
        fetchOptions = undefined;
    } else if (allowCaching || (!!tickers && tickers?.length < 100)) {
        fetchOptions = { next: { revalidate: 120 } };
    } else {
        // Returned values can be larger than 2MB, so we need to disable caching.
        fetchOptions = { cache: 'no-store' };
    }

    let fetchFailed = false;
    try {
        const eventsResponse = await (options?.retryTransientErrors
            ? fetchServerSide(url, fetchOptions, { retryTransientErrors: true })
            : fetchServerSide(url, fetchOptions));
        if (eventsResponse?.ok) {
            const eventsReturn = await eventsResponse.json();
            if (!!eventsReturn.events) {
                const returnedEvents: EventVo[] = eventsReturn.events;
                returnedEvents.forEach((event) => {
                    event.status = mostActiveStatus(event);
                    event.close_date = getCloseDate(event);
                });
                newEventsVo = returnedEvents.filter((event) => !eventsVo.find((e) => e.ticker === event.ticker));
            }
        } else if (!eventsResponse || eventsResponse.status !== 404) {
            // Only a genuine 404 is a real miss (the list endpoint returns
            // 200-empty for no results anyway). Every other non-OK status — null,
            // 5xx, or a transient 4xx like 429 (rate limited) — is a fetch failure:
            // treating a rate-limit as an empty list would let a cached metadata
            // path persist eventless/noindex metadata for a valid market.
            fetchFailed = true;
        }
    } catch (e) {
        const error = `Error fetching server-side data on Fetch Events from ${url}: ${e}`;
        logProviderFactory.logError(error);
        fetchFailed = true;
    }

    // Surface a transient fetch failure to opted-in callers so they can avoid
    // caching a failure-derived empty result (see `buildMarketMetadata`).
    if (fetchFailed && options?.throwOnFetchError) {
        throw new Error(`Events fetch failed for ${series_ticker ?? tickers?.join(',') ?? 'unknown'}`);
    }

    return newEventsVo;
};

export const fetchEventsByStructuredTargetIds = (structuredTargetIds: string[], page_size: number = 100) =>
    fetchServerSide(
        `${config.exchangeApiUrl}/v1/events/${buildQueryString({
            structured_target_ids: structuredTargetIds,
            page_size,
        })}`,
        {
            // Revalidate every 2 minutes
            next: { revalidate: 120 },
        },
    );

export const fetchEvent = async (
    ticker: string,
    // `allowUncachedRefetch` gates the internal pending-past-open re-fetch below.
    // Callers running inside an `unstable_cache` callback (the market metadata helper)
    // must pass false — its result would be frozen into the cache entry and re-served
    // for the whole revalidate window, so the extra round-trip buys nothing.
    // `staleRefetchRevalidate` chooses HOW that re-fetch reads. 0 (the default) bypasses
    // the data cache, which is only safe from a dynamic render: an uncached fetch inside
    // a prerender forces the whole route to render dynamically. Prerendered/ISR callers
    // pass a small positive value instead — still a cached read, so the route stays
    // static, and because a route's revalidate is the minimum of its fetches' it also
    // caps how long the stale pre-open page can be served.
    // `throwOnFetchError` surfaces a transient HTTP failure (null/non-OK response
    // or network error) as a throw instead of a silent `undefined`, so callers can
    // tell a failed fetch apart from a genuinely-missing event (an OK response with
    // no `event`, which still resolves `undefined`). Default off — existing callers
    // keep the swallow-to-undefined behavior.
    // `throwOnStaleRefreshFailure` extends that to the pending-past-open re-read below,
    // which is a different trade and so a different flag. The primary read failing leaves
    // nothing to render; the RE-READ failing leaves a stale-but-usable event, and throwing
    // it away is a worse outcome than showing it — unless the render is going to be STORED,
    // where the stale snapshot would be re-served for the whole cache window. So only
    // cache-safe callers opt in.
    // `retryTransientErrors` gives cache-safe runtime fills two short attempts before
    // the failure is surfaced. Request-scoped callers retain the normal single attempt.
    options: {
        revalidate?: number;
        noCache?: boolean;
        allowUncachedRefetch?: boolean;
        staleRefetchRevalidate?: number;
        throwOnFetchError?: boolean;
        throwOnStaleRefreshFailure?: boolean;
        retryTransientErrors?: boolean;
    } = {},
): Promise<EventVo | undefined> => {
    const {
        revalidate = 300,
        noCache = false,
        allowUncachedRefetch = true,
        staleRefetchRevalidate = 0,
        throwOnFetchError = false,
        throwOnStaleRefreshFailure = false,
        retryTransientErrors = false,
    } = options;
    const url = `${config.exchangeApiUrl}/v1/events/${ticker}?with_markdown=True`;

    // A stale Next data-cache entry can serve a pre-open snapshot of an event that is
    // already past its open time. Callers that detect this pass `noCache` to bypass the
    // data cache (`cache: 'no-store'`) and pull the fresh, now-open event. `no-store` and
    // `next.revalidate` are mutually exclusive in Next, so we pick exactly one.
    const fetchInit: RequestInit = noCache ? { cache: 'no-store' } : { next: { revalidate } };

    let eventVo: EventVo | undefined = undefined;
    // A null/non-OK primary response (or a thrown network error) is a transient
    // upstream failure — distinct from an OK response with no `event` (a genuinely
    // missing event). Tracked here so `throwOnFetchError` callers can surface it
    // without adding an extra error log for the non-OK case.
    let fetchFailed = false;

    try {
        const eventResponse = await (retryTransientErrors
            ? fetchServerSide(url, fetchInit, { retryTransientErrors: true })
            : fetchServerSide(url, fetchInit));
        if (!eventResponse?.ok) {
            // Only a genuine 404 ("no such event") resolves undefined so opted-in
            // callers can fall back rather than 500 the route. Every other non-OK
            // status — null, 5xx, or a transient 4xx like 429 (rate limited) — is a
            // fetch failure: treating a rate-limit as a miss would let a cached
            // metadata path persist generic/noindex metadata for a valid event.
            if (!eventResponse || eventResponse.status !== 404) {
                fetchFailed = true;
            }
        } else {
            let eventReturn = await eventResponse.json();
            if (!!eventReturn.event) {
                // Check if the event start date has passed and the state is still pending, if so, we make a new uncached request
                const eventObject: EventVo = eventReturn.event;
                const shouldRevalidate = hasPendingPastOpenMarket(eventObject);

                if (shouldRevalidate && allowUncachedRefetch) {
                    const refreshOptions = { next: { revalidate: staleRefetchRevalidate } };
                    const refreshResponse = await (retryTransientErrors
                        ? fetchServerSide(url, refreshOptions, { retryTransientErrors: true })
                        : fetchServerSide(url, refreshOptions));
                    if (refreshResponse?.ok) {
                        eventReturn = await refreshResponse.json();
                    } else if (throwOnStaleRefreshFailure && (!refreshResponse || refreshResponse.status !== 404)) {
                        // Only for callers whose render gets stored, where the pending
                        // snapshot this refresh exists to replace would be re-served for the
                        // whole cache window. Everyone else keeps the primary event: it is
                        // stale but usable, and discarding it degrades the page harder than
                        // the staleness does. Same 404 carve-out as the primary read above.
                        fetchFailed = true;
                    }
                }

                eventVo = eventReturn.event;
            }
        }
    } catch (e) {
        const error = `Error fetching server-side data on Fetch Event from ${url}: ${e}`;
        logProviderFactory.logError(error);
        fetchFailed = true;
    }

    // Surface a transient fetch failure to opted-in callers so they can avoid
    // caching a failure-derived "missing event" result (see `buildMarketMetadata`).
    if (fetchFailed && throwOnFetchError) {
        throw new Error(`Event fetch failed for ${ticker}`);
    }

    if (eventVo) {
        eventVo.status = mostActiveStatus(eventVo);
        eventVo.close_date = getCloseDate(eventVo);
    }
    return eventVo;
};

export const fetchCachedEvent = async (ticker: string): Promise<EventVo | undefined> => {
    const url = `${config.exchangeApiUrl}/v1/cached/events/${buildQueryString({ tickers: [ticker] })}`;
    try {
        const response = await fetchServerSide(url, {
            // Revalidate every 2 minutes
            next: { revalidate: 120 },
        });
        if (!response?.ok) {
            drainResponseBody(response);
            return undefined;
        }
        const data: GetEventsResponse = await response.json();
        return data.events[0];
    } catch (e) {
        logProviderFactory.logError(`Error fetching cached event ${ticker}: ${e}`);
        return undefined;
    }
};

export interface ForecastHistoryOptions {
    /** Hit /v1/series/... (uncached) instead of /v1/cached/... — pair with a shorter `revalidate`. */
    fresh?: boolean;
    /** Round timestamps to minute boundaries so revalidate cache keys actually collapse. */
    alignToMinute?: boolean;
    /** Next.js fetch cache TTL in seconds. */
    revalidate?: number;
    /**
     * Re-throw on non-OK response or network error instead of silently returning [].
     * Pass true from React Query queryFn so isError is set and the baseline fallback triggers.
     */
    throwOnError?: boolean;
}

export const fetchMarketForecastHistory = async (
    seriesTicker: string,
    marketId: string,
    startTs: number,
    endTs: number,
    periodInterval: HistoricalDataPeriodInterval,
    options: ForecastHistoryOptions = {},
): Promise<MarketForecastHistoryVo[]> => {
    const { fresh = false, alignToMinute = false, revalidate = 3600, throwOnError = false } = options;
    const start = alignToMinute ? startTs - (startTs % 60) + 60 : startTs;
    const end = alignToMinute ? endTs - (endTs % 60) : endTs;
    const pathPrefix = fresh ? 'v1/series' : 'v1/cached/series';

    let url = `${config.exchangeApiUrl}/${pathPrefix}/${seriesTicker}/markets/${marketId}/forecast_history`;
    url += `?start_ts=${start}`;
    url += `&end_ts=${end}`;
    url += `&period_interval=${periodInterval}`;
    url += '&candlestick_function=mean_price';

    let forecastHistory: MarketForecastHistoryVo[] = [];
    let caughtError: unknown = null;

    try {
        const response = await fetchServerSide(url, { next: { revalidate } });
        if (response?.ok) {
            const forecastReturn = await response.json();
            if (!!forecastReturn.forecast_history) {
                forecastHistory = forecastReturn.forecast_history;
            }
        } else if (throwOnError) {
            caughtError = new Error(`forecast_history fetch failed: HTTP ${response?.status ?? 'no response'}`);
        }
    } catch (e) {
        if (throwOnError) {
            // Live/game fallback path: caller (React Query) handles the error;
            // chart falls back to baseline. Error-level logging here would fire on every
            // failed poll and trigger Datadog alerts for an expected, handled condition.
            caughtError = e;
        } else {
            logProviderFactory.logError(`Error fetching server-side data on Market Forecast History from ${url}: ${e}`);
        }
    }

    if (caughtError) throw caughtError;

    return forecastHistory;
};

export const fetchEventForecastHistory = async (
    seriesTicker: string,
    eventTicker: string,
    startTs: number,
    endTs: number,
    periodInterval: HistoricalDataPeriodInterval,
    options: ForecastHistoryOptions = {},
): Promise<MarketForecastHistoryVo[]> => {
    const { fresh = false, alignToMinute = false, revalidate = 3600, throwOnError = false } = options;
    const start = alignToMinute ? startTs - (startTs % 60) : startTs;
    const end = alignToMinute ? endTs - (endTs % 60) : endTs;
    const pathPrefix = fresh ? 'v1/series' : 'v1/cached/series';
    let url = `${config.exchangeApiUrl}/${pathPrefix}/${seriesTicker}/events/${eventTicker}/forecast_history`;

    url += `?start_ts=${start}`;
    url += `&end_ts=${end}`;
    url += `&period_interval=${periodInterval}`;

    let forecastHistory: MarketForecastHistoryVo[] = [];
    let caughtError: unknown = null;

    try {
        const response = await fetchServerSide(url, { next: { revalidate } });
        if (response?.ok) {
            const forecastReturn = await response.json();
            if (!!forecastReturn.forecast_history) {
                forecastHistory = forecastReturn.forecast_history;
            }
        } else if (throwOnError) {
            caughtError = new Error(`event forecast_history fetch failed: HTTP ${response?.status ?? 'no response'}`);
        }
    } catch (e) {
        if (throwOnError) caughtError = e;
        else logProviderFactory.logError(`Error fetching server-side data on Event Forecast History from ${url}: ${e}`);
    }

    if (caughtError) throw caughtError;
    return forecastHistory;
};

export const fetchMarketCandlesticksHistory = async (
    seriesTicker: string,
    marketId: string,
    startTs: number,
    endTs: number,
    periodInterval: HistoricalDataPeriodInterval,
    options: Pick<ForecastHistoryOptions, 'revalidate' | 'throwOnError'> = {},
): Promise<MarketCandlestickVo[]> => {
    const { revalidate = 60, throwOnError = false } = options;
    const start = startTs - (startTs % 60);
    const end = endTs - (endTs % 60);
    let url = `${config.exchangeApiUrl}/v1/series/${seriesTicker}/markets/${marketId}/candlesticks`;
    url += `?start_ts=${start}&end_ts=${end}&period_interval=${periodInterval}`;

    try {
        const response = await fetchServerSide(url, { next: { revalidate } });
        if (response?.ok) {
            const payload = await response.json();
            return payload.candlesticks ?? [];
        }
        if (throwOnError) {
            throw new Error(`candlesticks fetch failed: HTTP ${response?.status ?? 'no response'}`);
        }
    } catch (error) {
        if (throwOnError) throw error;
        logProviderFactory.logError(`Error fetching server-side Market Candlesticks History from ${url}: ${error}`);
    }
    return [];
};

// The pre-footer's event columns slice 16 links out of a single page of these
// two searches. Deliberately kept at 25 rather than over-fetching to leave room
// for the Footer's client-side prop filter: despite the name, `fetchServerSide`
// resolves to a plain browser `fetch` here (the Footer is a client component
// pulling these through React Query), so `next.revalidate` below buys no ISR
// caching and every extra result is paid per visitor. Props are excluded in the
// request instead — see `include_sports_derivatives` — so the client-side filter
// has nothing left to trim and cannot shorten a column.
const FOOTER_SEARCH_PAGE_SIZE = 25;

export const fetchNewestEventsSeries = (excludedCategories?: SearchSeriesParams['excluded_categories']) =>
    fetchServerSide(
        `${config.exchangeApiUrl}/v1/search/series${buildQueryString({
            order_by: 'newest-event',
            excluded_categories: excludedCategories,
            status: 'open',
            page_size: FOOTER_SEARCH_PAGE_SIZE,
            with_milestones: true,
            // Keep high-frequency series (15-min / hourly) out of the footer's
            // "New markets" column. The search API has no exclude param, so pass
            // the full frequency allow-list minus `fifteen_min` and `hourly`.
            // Arrays serialize as comma-separated values via buildQueryString.
            frequency: ['one_off', 'one_time', 'daily', 'weekly', 'monthly', 'quarterly', 'annual', 'custom'],
            // Sports derivatives (spread/total/player-prop events hung off a
            // game) are derivatives of the primary game page and never earn a
            // pre-footer link. Excluding them here rather than after the fact is
            // what keeps a prop-heavy page from shortening the columns. This is
            // the search API's default, pinned explicitly so the footer's
            // exclusion doesn't rest on it.
            include_sports_derivatives: false,
        })}`,
        { next: { revalidate: 120 } },
    );

export const fetchSeriesByCategory = (category: string) =>
    fetchServerSide(
        `${config.exchangeApiUrl}/v1/search/series${buildQueryString({
            order_by: 'trending',
            status: 'open',
            page_size: FOOTER_SEARCH_PAGE_SIZE,
            category,
            with_milestones: true,
            // Include structured_targets so the public footer's "Related topics"
            // can derive Teams page links for sports series without an extra
            // round-trip per team.
            hydrate: 'structured_targets',
            // See fetchNewestEventsSeries — props are excluded server-side too.
            include_sports_derivatives: false,
        })}`,
        { next: { revalidate: 120 } },
    );

export const BFF_PUBLIC_FEED_TAG = 'bff-public-feed';

export const fetchBffPublicFeed = async (
    query?: GetPublicFeedRequestQuery,
    options: { throwOnError?: boolean } = {},
): Promise<GetPublicFeedResponse> => {
    const response = await fetchServerSide(`${config.exchangeApiUrl}/v1/bff/feed${buildQueryString(query)}`, {
        next: { revalidate: 120, tags: [BFF_PUBLIC_FEED_TAG] },
    });
    if (!response?.ok) {
        drainResponseBody(response);
        // Opt-in for cached server callers: throwing keeps a transient failure OUT of the
        // `unstable_cache`/ISR entry (a failed fill would otherwise cache an empty feed). Default
        // stays swallow-to-fallback for client/non-cached callers.
        if (options.throwOnError) {
            throw new Error(`BFF public feed request failed${response ? ` with status ${response.status}` : ''}`);
        }
        return { sections: [], hydrated_data: { milestones: {}, structured_targets: {}, keywords: {} } };
    }
    return await response.json();
};

export const BFF_PUBLIC_HOME_TAG = 'bff-public-home';

export const fetchPublicHome = async (options: HomeRequest): Promise<GetPublicHomeResponse> => {
    const response = await fetchServerSide(
        `${config.exchangeApiUrl}/v1/bff/home${homeQueryString(options)}`,
        { next: { revalidate: 120, tags: [BFF_PUBLIC_HOME_TAG] } },
        { retryTransientErrors: false },
    );
    if (!response?.ok) {
        drainResponseBody(response);
        throw new Error(`BFF public home request failed${response ? ` with status ${response.status}` : ''}`);
    }
    return enrichHomeResponseWithRelations(await response.json());
};
